// WALLET-P1 §6.1: Spend — By agent · By task · By day · By category for a month
// (`wallet.spend`; the API names each category), a dense table in dollars; a row opens the
// detail drawer.
import {
  buttonVariants,
  cn,
  EmptyState,
  InfoTip,
  NativeSelect,
  NativeSelectOption,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  ViewTabs,
} from "@companyos/ui";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { BotIcon, ChartColumnIcon, ChevronRightIcon } from "lucide-react";
import { useMemo, useState } from "react";
import type { Api } from "../../api/client";
import { keepFocus } from "../../focus/focus";
import type { CompanyAccess } from "../company";
import { formatDate } from "../format";
import { LoadError, Loading } from "../screen-parts";
import { DENSE_TABLE, RowAction, RowActions } from "../settings/row-actions";
import { dollars } from "./billing-money";
import {
  monthName,
  recentUtcMonths,
  type SpendGroup,
  spendQuery,
  WALLET_POLL_MS,
  type WalletSpend,
} from "./billing-queries";

const TABS: { value: SpendGroup; label: string; head: string }[] = [
  { value: "agent", label: "By agent", head: "Agent" },
  { value: "task", label: "By task", head: "Task" },
  { value: "day", label: "By day", head: "Day" },
  { value: "category", label: "By category", head: "Category" },
];

export function SpendTabs({
  api,
  company,
  onOpen,
}: {
  api: Api;
  company: CompanyAccess;
  onOpen: (group: SpendGroup, period: string, row: WalletSpend["rows"][number]) => void;
}) {
  const months = useMemo(() => recentUtcMonths(new Date()), []);
  const [period, setPeriod] = useState(months[0] ?? "");
  const [group, setGroup] = useState<SpendGroup>("agent");
  const spend = useQuery({
    ...spendQuery(api, company.id, period, group),
    refetchInterval: WALLET_POLL_MS,
  });
  const head = TABS.find((t) => t.value === group)?.head ?? "";
  return (
    <section aria-labelledby="billing-spend" className="flex min-w-0 flex-col gap-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 id="billing-spend" className="text-sm font-semibold">
          Spend
        </h2>
        <div className="flex items-center gap-1">
          {/* SPEND-KPIS: per-DE day / week / month spend, averages, budget pace. */}
          <Link
            to="/c/$slug/kpis/reports/finance/agent-spend"
            params={{ slug: company.slug }}
            search={keepFocus}
            data-slot="agent-spend-link"
            className={cn(buttonVariants({ variant: "ghost", size: "sm" }))}
          >
            <BotIcon aria-hidden="true" />
            Agent spend
          </Link>
          <NativeSelect
            aria-label="Month"
            size="sm"
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
          >
            {months.map((m) => (
              <NativeSelectOption key={m} value={m}>
                {monthName(m)}
              </NativeSelectOption>
            ))}
          </NativeSelect>
        </div>
      </div>
      <ViewTabs
        aria-label="Spend by"
        tabs={TABS.map(({ value, label }) => ({ value, label }))}
        value={group}
        onValueChange={(v) => setGroup(v as SpendGroup)}
      />
      {spend.isPending ? (
        <Loading label="Loading spend" rows={3} />
      ) : spend.isError ? (
        <LoadError what="Spend" error={spend.error} />
      ) : spend.data.rows.length === 0 ? (
        <EmptyState
          variant="card"
          icon={ChartColumnIcon}
          title={`Nothing spent in ${monthName(period)}`}
        />
      ) : (
        <Table className={DENSE_TABLE} data-slot="spend-table" data-group={group}>
          <TableHeader>
            <TableRow>
              <TableHead scope="col">{head}</TableHead>
              <TableHead scope="col" className="text-right">
                Runs
              </TableHead>
              <TableHead scope="col" className="text-right">
                <span className="inline-flex items-center gap-1">
                  Amount
                  <InfoTip label="About the amount">
                    Charged at our engine rate; see What things cost.
                  </InfoTip>
                </span>
              </TableHead>
              <TableHead scope="col">
                <span className="sr-only">Actions</span>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {spend.data.rows.map((r) => {
              const label = group === "day" ? formatDate(r.label) : r.label;
              return (
                <TableRow
                  key={r.key}
                  className="group/row cursor-pointer"
                  data-spend={r.key}
                  onClick={() => onOpen(group, period, r)}
                >
                  <TableHead scope="row" className="font-normal">
                    {label}
                  </TableHead>
                  <TableCell className="text-right tabular-nums">{r.runs}</TableCell>
                  <TableCell className="text-right tabular-nums">
                    {dollars(r.charged_micros)}
                  </TableCell>
                  <TableCell className="w-px text-right">
                    <RowActions>
                      <RowAction
                        icon={ChevronRightIcon}
                        label={`Details of ${label}`}
                        tip="Details"
                        onClick={(e) => {
                          e.stopPropagation();
                          onOpen(group, period, r);
                        }}
                      />
                    </RowActions>
                  </TableCell>
                </TableRow>
              );
            })}
            <TableRow data-slot="spend-total">
              <TableHead scope="row">Total</TableHead>
              <TableCell />
              <TableCell className="text-right font-medium tabular-nums">
                {dollars(spend.data.total_micros)}
              </TableCell>
              <TableCell />
            </TableRow>
          </TableBody>
        </Table>
      )}
    </section>
  );
}
